import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { IDataImportService, SubmissionStatus, TemplateType } from "services/DataImportService";
import { RootState } from "setup";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import { acquireSubmissionLock } from "./lockThunks";
import { writeErrorMessage } from "./submissionErrors";
import { toSubmissionModel } from "./submissionModel";
import { downloadBundleBlob } from "../wizard/steps/Validation/download/annotatedDownload";

// Shared submission thunks — SubmissionsListSlice and ActiveSubmissionSlice both react to these.
// Failure toasts live here (side effects are banned in reducers — DevTools time-travel and
// hot-reload replays would re-fire them); the slices' rejected reducers only flip loading flags.

const dataImportService = () => container.get<IDataImportService>(SERVICE_TYPES.DataImportService);

// The BE filter takes a single submissionStatus only, so fetch everything once and split
// in-progress vs past client-side.
export const getSubmissions = createAsyncThunk("DataImport/GetSubmissions", async () => {
  try {
    return (await dataImportService().getSubmissions()).map(toSubmissionModel);
  } catch (error) {
    toast.error("Failed to load submissions");
    throw error;
  }
});

export const getTemplateUrls = createAsyncThunk(
  "DataImport/GetTemplateUrls",
  async () => {
    return await dataImportService().getTemplateUrls();
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      return (
        state.dataImportSubmissionsList.TemplateUrls === null && !state.dataImportSubmissionsList.LoadingTemplateUrls
      );
    },
  }
);

export const downloadOriginalFiles = createAsyncThunk(
  "DataImport/DownloadOriginalFiles",
  async (submissionID: string) => {
    const files = await dataImportService().getSubmissionFiles(submissionID);
    if (files.length === 0) {
      throw new Error("Submission has no uploaded files");
    }

    for (const file of files) {
      if (!file.submissionFileID) continue;
      const response = await dataImportService().downloadSubmissionFile(file.submissionFileID);
      downloadBundleBlob(response.data, response.fileName ?? file.fileName ?? `${file.submissionFileID}.xlsx`);
    }
  }
);

export const getActiveSubmission = createAsyncThunk("DataImport/GetActiveSubmission", async (submissionID: string) => {
  return toSubmissionModel(await dataImportService().getSubmission(submissionID));
});

export const createSubmission = createAsyncThunk(
  "DataImport/CreateSubmission",
  async (arg: { templateType: TemplateType; parentSubmissionID?: string }, { dispatch }) => {
    try {
      const submission = toSubmissionModel(
        await dataImportService().createSubmission(arg.templateType, arg.parentSubmissionID)
      );
      // The creator always wins the lock on a fresh submission; the wizard-mount acquire renews
      // it, so a transient failure here never fails the create.
      await dispatch(acquireSubmissionLock(submission.submissionID));
      return submission;
    } catch (error) {
      toast.error("Failed to create submission");
      throw error;
    }
  }
);

export const updateSubmissionStep = createAsyncThunk(
  "DataImport/UpdateSubmissionStep",
  async (arg: { submissionID: string; submissionStatus: SubmissionStatus; currentStep: number }) => {
    try {
      return toSubmissionModel(
        await dataImportService().updateSubmissionStep(arg.submissionID, arg.submissionStatus, arg.currentStep)
      );
    } catch (error) {
      toast.error(writeErrorMessage(error, "Failed to save your progress"));
      throw error;
    }
  }
);

export const submitSubmission = createAsyncThunk(
  "DataImport/SubmitSubmission",
  async (arg: { submissionID: string; bundle: Blob; bundleFileName: string }) => {
    try {
      return toSubmissionModel(
        await dataImportService().submitSubmission(arg.submissionID, arg.bundle, arg.bundleFileName)
      );
    } catch (error) {
      toast.error(writeErrorMessage(error, "Failed to submit your import"));
      throw error;
    }
  }
);

// Discarding closes the submission rather than deleting it — the record is kept for audit and
// shows up under Past Submissions.
export const closeSubmission = createAsyncThunk("DataImport/CloseSubmission", async (submissionID: string) => {
  try {
    return toSubmissionModel(await dataImportService().closeSubmission(submissionID));
  } catch (error) {
    toast.error(writeErrorMessage(error, "Failed to discard submission"));
    throw error;
  }
});
